import React, { useState, useEffect, useRef, useMemo } from 'react';
import Base from '../Base';
import {
    Container,
    Row,
    Col,
    Breadcrumb,
    Card,
    ProgressBar,
    Table,
    Button,
    Spinner
} from "react-bootstrap";
import { NavLink } from "react-router-dom";
import {
    FiUpload,
    FiCheckCircle,
    FiAlertTriangle,
    FiXCircle,
    FiAlertCircle,
    FiBarChart,
    FiPlus
} from 'react-icons/fi';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    DoughnutController
} from 'chart.js';
import BRCFileUploadModal from './BRCFileUploadModal';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getStatusDisplay } from './BRCHelper';
import { calculateMetricsPercentages, mapUploadTrendsWithVariants } from './BRCHelper';
import instance from "../../services/baseService";

// Register Chart.js components
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    DoughnutController
);

function BrcDashboard() {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for all dashboard data
    const [dashboardData, setDashboardData] = useState({
        metrics: {
            totalUploads: 0,
            matched: 0,
            partial: 0,
            missing: 0
        },
        uploadTrends: [],
        reconciliationData: [],
        recentUploads: [],
        totalCount: 0
    });

    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const navigate = useNavigate();
    const { client_id } = useParams();

    // Function to determine color based on label
    const getColorFromLabel = (label) => {
        const lowerLabel = label.toLowerCase();
        if (lowerLabel.includes('matched') || lowerLabel.includes('completed') || lowerLabel.includes('success')) {
            return 'success';
        } else if (lowerLabel.includes('almost') || lowerLabel.includes('previous') || lowerLabel.includes('warning') || lowerLabel.includes('partial')) {
            return 'warning';
        } else if (lowerLabel.includes('missing') || lowerLabel.includes('failed') || lowerLabel.includes('error')) {
            return 'danger';
        }
        return 'secondary';
    };

    // API call function
    const fetchDashboardData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await instance.get(
                `/api/v1/reconciliation/dashboard/${client_id}`,
            );
            // if (!response.ok) {
            //     throw new Error(`HTTP error! status: ${response.status}`);
            // }
            const result = await response.data;

            if (result.type === 'success' && result.data) {
                // Map the API response to match component structure
                const mappedData = {
                    metrics: {
                        totalUploads: result.data.metrics.totalUploads,
                        matched: result.data.metrics.matched,
                        partial: result.data.metrics.partial,
                        missing: result.data.metrics.missing
                    },
                    uploadTrends: mapUploadTrendsWithVariants(result.data.uploadTrends),
                    reconciliationData: result.data.reconciliationData.map(item => ({
                        ...item,
                        color: getColorFromLabel(item.label)
                    })),
                    recentUploads: result.data.recentUploads,
                    totalCount: result.data.totalCount
                };

                setDashboardData(mappedData);
            } else {
                throw new Error(result.message || 'Failed to fetch dashboard data');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message);

            // Set empty data structure on error
            setDashboardData({
                metrics: {
                    totalUploads: 0,
                    matched: 0,
                    partial: 0,
                    missing: 0
                },
                uploadTrends: [],
                reconciliationData: [],
                recentUploads: [],
                totalCount: 0
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        if (client_id) {
            fetchDashboardData();
        }
    }, [client_id]);

    // Calculate percentages for cards display
    const metricsPercentages = useMemo(() => {
        return calculateMetricsPercentages(dashboardData.metrics);
    }, [dashboardData.metrics]);

    // Icon mapping for upload trends
    const getIconForTrend = (variant) => {
        switch (variant) {
            case 'success': return FiCheckCircle;
            case 'warning': return FiAlertTriangle;
            case 'danger': return FiAlertCircle;
            default: return FiAlertCircle;
        }
    };

    // Enhanced upload trends data with icons
    const uploadTrendsData = useMemo(() => {
        return dashboardData.uploadTrends.map(item => ({
            ...item,
            icon: getIconForTrend(item.variant)
        }));
    }, [dashboardData.uploadTrends]);

    const reconciliationData = useMemo(() => dashboardData.reconciliationData, [dashboardData.reconciliationData]);

    // Color mapping for chart
    const getChartColor = (color) => {
        switch (color) {
            case 'success': return '#28a745';
            case 'warning': return '#ffc107';
            case 'danger': return '#dc3545';
            default: return '#6c757d';
        }
    };

    // Calculate total records
    const totalRecords = reconciliationData.reduce((sum, item) => sum + item.count, 0);

    // Initialize Chart.js doughnut chart
    useEffect(() => {
        if (!isLoading && chartRef.current && reconciliationData.length > 0) {
            const ctx = chartRef.current.getContext('2d');

            // Destroy existing chart if it exists
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }

            const chartData = {
                labels: reconciliationData.map(item => item.label),
                datasets: [{
                    data: reconciliationData.map(item => item.count),
                    backgroundColor: reconciliationData.map(item => getChartColor(item.color)),
                    borderWidth: 2,
                    borderColor: '#ffffff',
                    hoverBorderWidth: 3,
                    hoverBorderColor: '#ffffff'
                }]
            };

            const config = {
                type: 'doughnut',
                data: chartData,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '65%',
                    plugins: {
                        legend: {
                            display: false
                        },
                        tooltip: {
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            titleColor: '#ffffff',
                            bodyColor: '#ffffff',
                            borderColor: '#ffffff',
                            borderWidth: 1,
                            cornerRadius: 6,
                            displayColors: true,
                            callbacks: {
                                label: function (context) {
                                    const label = context.label || '';
                                    const value = context.parsed;
                                    const percentage = reconciliationData[context.dataIndex].percentage;
                                    return `${label}: ${value} (${percentage}%)`;
                                }
                            }
                        }
                    },
                    onHover: (event, activeElements) => {
                        event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
                    }
                },
                plugins: [{
                    id: 'centerText',
                    beforeDraw: function (chart) {
                        const ctx = chart.ctx;
                        const centerX = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
                        const centerY = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;

                        ctx.save();
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';

                        // Draw total number
                        ctx.font = 'bold 2.5rem Arial';
                        ctx.fillStyle = '#212529';
                        ctx.fillText(totalRecords.toString(), centerX, centerY - 15);

                        // Draw subtitle
                        ctx.font = '0.875rem Arial';
                        ctx.fillStyle = '#6c757d';
                        ctx.fillText('Total Records', centerX, centerY + 15);
                        ctx.fillText('Processed', centerX, centerY + 35);

                        ctx.restore();
                    }
                }]
            };

            chartInstanceRef.current = new ChartJS(ctx, config);
        }

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [isLoading, reconciliationData, totalRecords]);

    // Loading overlay component
    if (isLoading) {
        return (
            <Base>
                <Container fluid className="p-4 bg-light min-vh-100">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    </div>
                </Container>
            </Base>
        );
    }

    // Error state
    // if (error) {
    //     return (
    //         <Base>
    //             <Container fluid className="p-4 bg-light min-vh-100">
    //                 <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
    //                     <div className="text-center">
    //                         <FiAlertCircle size={48} className="text-danger mb-3" />
    //                         <h4 className="text-danger">Error Loading Dashboard</h4>
    //                         <p className="text-muted">{error}</p>
    //                     </div>
    //                 </div>
    //             </Container>
    //         </Base>
    //     );
    // }

    return (
        <Base>
            <Container fluid className="p-4 bg-light min-vh-100">
                <Row>
                    <Col xs={12}>
                        <Breadcrumb>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/" }}>
                                Dashboard
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/clients" }}>
                                Clients
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                linkAs={NavLink}
                                linkProps={{ to: `/clients/tax-forms/${client_id}` }}
                            >
                                Tax-Forms
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>RoDTEP/RoSCTL</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Row className="align-items-center justify-content-between mb-3">
                            <Col>
                                <h2 className="fw-bold mb-2">RoDTEP/RoSCTL</h2>
                                <p className="text-muted mb-0">Overview of data reconciliation metrics and trends.</p>
                            </Col>

                            <Col className="col-auto">
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        className="no-hover fw-bold d-flex align-items-center justify-content-center"
                                        style={{ width: '150px' }}
                                        onClick={() => navigate(`/RoDTEP/RoSCTL/status/${client_id}`)}
                                    >
                                        <FiBarChart className="me-2" />
                                        Upload Status
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="no-hover fw-bold d-flex align-items-center justify-content-center"
                                        style={{ width: '150px' }}
                                        onClick={() => setShowUploadModal(true)}
                                    >
                                        <FiPlus className="me-2" />
                                        Upload Files
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        {/* Metrics Cards */}
                        <Row>
                            <Col lg={3} md={6} className="mb-3 ">
                                <Card className="metric-card shadow-sm h-80">
                                    <Card.Body className='mb-0'>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-1">
                                                    <FiUpload className="text-muted me-2" />
                                                    <small className="text-muted">Total Uploads</small>
                                                </div>
                                                <h2 className="fw-bold mb-0">{dashboardData.metrics.totalUploads}</h2>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="metric-card shadow-sm h-80">
                                    <Card.Body>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-1">
                                                    <FiCheckCircle className="text-success me-2" />
                                                    <small className="text-muted">Matched</small>
                                                </div>
                                                <h2 className="fw-bold mb-0">{metricsPercentages.matchedPercent}%</h2>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="metric-card shadow-sm h-80">
                                    <Card.Body>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-1">
                                                    <FiAlertTriangle className="text-warning me-2" />
                                                    <small className="text-muted">Almost</small>
                                                </div>
                                                <h2 className="fw-bold mb-0">{metricsPercentages.partialPercent}%</h2>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="metric-card shadow-sm h-80">
                                    <Card.Body>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-1">
                                                    <FiXCircle className="text-danger me-2" />
                                                    <small className="text-muted">Missing</small>
                                                </div>
                                                <h2 className="fw-bold mb-0">{metricsPercentages.missingPercent}%</h2>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Charts Row */}
                        <Row className="mb-2">
                            {/* Upload Trends */}
                            <Col lg={6} className="mb-3 d-flex">
                                <Card className="border-0 w-100">
                                    <Card.Body className="d-flex flex-column">
                                        <h5 className="fw-semibold mb-4">Upload Trends</h5>
                                        <div className="flex-grow-1">
                                            {uploadTrendsData.length > 0 ? (
                                                uploadTrendsData.map((item, index) => {
                                                    const IconComponent = item.icon;
                                                    return (
                                                        <div key={index} className="mb-3">
                                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                                <div className="d-flex align-items-center">
                                                                    <IconComponent className={`text-${item.variant} me-2`} />
                                                                    <span>{item.label}</span>
                                                                </div>
                                                                <span className="text-muted">{item.percentage}%</span>
                                                            </div>
                                                            <ProgressBar
                                                                variant={item.variant}
                                                                now={item.percentage}
                                                                className="custom-progress"
                                                            />
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="text-center py-4">
                                                    <p className="text-muted">No upload trends data available.</p>
                                                </div>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Reconciliation Status with Responsive Doughnut Chart */}
                            <Col lg={6} className="mb-3 d-flex">
                                <Card className="border-0 w-100">
                                    <Card.Header className="bg-white border-0 pb-0">
                                        <h5 className="fw-semibold mb-0">Reconciliation Status</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        {reconciliationData.length > 0 ? (
                                            <div className="row h-100 align-items-center">
                                                {/* Legend */}
                                                <div className="col-md-5 col-lg-4 mb-3 mb-md-0">
                                                    <div className="d-flex d-md-block">
                                                        <div className="row">
                                                            {reconciliationData.map((item, index) => (
                                                                <div key={index} className="col-6 col-md-12 mb-2">
                                                                    <div className="d-flex align-items-center">
                                                                        <span
                                                                            className="me-2 flex-shrink-0"
                                                                            style={{
                                                                                width: '12px',
                                                                                height: '12px',
                                                                                backgroundColor: getChartColor(item.color),
                                                                                borderRadius: '50%',
                                                                                display: 'inline-block',
                                                                            }}
                                                                        ></span>
                                                                        <div className="flex-grow-1 d-flex justify-content-between">
                                                                            <div className="small">{item.label}</div>
                                                                            <div className="fw-bold small">
                                                                                {item.count} ({item.percentage}%)
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Chart */}
                                                <div className="col-md-7 col-lg-8">
                                                    <div
                                                        className="position-relative d-flex justify-content-center align-items-center"
                                                        style={{ height: '250px' }}
                                                    >
                                                        <canvas
                                                            ref={chartRef}
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '100%'
                                                            }}
                                                        ></canvas>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <p className="text-muted">No reconciliation data available.</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Recent Uploads */}
                        <Row>
                            <Col xs={12}>
                                <Card className="shadow-sm">
                                    <Card.Body>
                                        <h5 className="fw-semibold mb-4">Recent Uploads</h5>
                                        <div className="table-responsive">
                                            <Table hover>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>USER NAME</th>
                                                        <th>STATUS</th>
                                                        <th>MATCHED</th>
                                                        <th>PARTIAL</th>
                                                        <th>MISSING</th>
                                                        <th>Created DATE</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dashboardData.recentUploads.map((upload, index) => (
                                                        <tr key={upload.id || index}>
                                                            <td className='text-left'>
                                                                {upload.status === 1 ? (<Link to={`/RoDTEP/RoSCTL/reports/${client_id}/${upload.id}`} className="text-primary text-decoration-none">
                                                                    {upload.userName}
                                                                </Link>) :
                                                                    (upload.userName)}

                                                            </td>
                                                            <td className='font-bold text-left'>
                                                                {getStatusDisplay(upload.status)}
                                                            </td>
                                                            <td className='text-left'>{upload.matched}</td>
                                                            <td className='text-left'>{upload.partial}</td>
                                                            <td className='text-left'>{upload.missing}</td>
                                                            <td className='text-left'>{upload.created_date}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                        {dashboardData.recentUploads.length === 0 && (
                                            <div className="text-center py-4">
                                                <p className="text-muted">No recent uploads found.</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
            <BRCFileUploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                clientId={client_id}
                reFetch={fetchDashboardData}
            />
        </Base>
    );
}

export default BrcDashboard;